import { useCallback } from 'react';

import openHelpAgentPopup from 'modules/IMC/HelpAgent/openHelpAgentPopup';
import useApiAccessToken from 'modules/Auth/AuthProvider/useApiAccessToken';
import { useMember } from 'modules/common/queries/member';
import useStatsigGate from 'modules/common/useStatsigGate';
import { FeatureGates } from 'modules/common/constants/featuregates';
import useIMC from 'modules/IMC/useIMC';
import useGenerateSignature from 'services/ai/mutations/useGenerateSignature';
import useNewConversation from 'services/ai/mutations/conversations';
import { sendDatadogRumError } from 'utils/analytics/datadogRum';

/**
 * Conversation metadata key the Accounting Setup AOP gates its visibility on. The AOP is
 * configured Decagon-side to only surface when this equals ACCOUNTING_SETUP_PAGE, so an
 * entrypoint that omits it reaches the generic flow instead and answers nothing useful.
 *
 * `/ai/member/conversations` applies no serializer or allowlist to `metadata` — it merges the
 * dict straight through to Decagon's /conversation/new — so this needs no backend change.
 */
const ACCOUNTING_SETUP_PAGE = 'accounting-setup';

/**
 * Names the AOP Decagon should run, and must equal the literal in that AOP's own condition.
 *
 * `aop_name` is not a Decagon built-in — it is a plain custom metadata key each AOP conditions on
 * itself (the transaction AOP's condition is literally `metadata.aop_name == "txn_help"`), so the
 * only contract is that this string and the Decagon-side condition agree. Lowercase snake to
 * match `txn_help`.
 *
 * `page` is deliberately NOT the routing key: it has one value for the whole wizard, so it can
 * never distinguish two questions on the same screen — which is the point of AP-2448. It still
 * travels as metadata, for analytics grouping.
 *
 * If the condition is ever re-authored, this string has to move with it — nothing fails loudly,
 * the entrypoint just silently falls back to the default AOP.
 */
const ACCOUNTING_SETUP_AOP_NAME = 'accounting_setup_help';

/**
 * Scene-setting clause the member's own question is appended to.
 *
 * Phrased as a statement of where the member is, NOT as a request. "I need help with my accounting
 * setup." was read as an ask in its own right, so the agent saw two unrelated requests and split
 * its answer between them ("It looks like your question covers two things..."). A clause that only
 * establishes context leaves exactly one question in the message.
 *
 * Also ranking insurance: naming the surface denies the default AOP a topical match on questions
 * like "What should I expect during this setup?", which it had been answering from the payroll
 * setup guide. Redundant once the AOP is bound by force selection on `aop_name`, and kept anyway
 * so a mis-authored condition degrades to a worse answer rather than a wrong one.
 */
const ACCOUNTING_SETUP_OPENER = "I'm going through the Collective accounting setup.";

/**
 * Which step of the wizard opened the help agent, reported to Decagon as conversation metadata.
 *
 * Deliberately a closed set rather than a free string: `question` already carries the specific
 * text a member clicked, so this is the coarse axis analytics groups by, and a typo'd or drifting
 * label ("Connect Statements" vs "connect-statements") silently splits a step in two.
 *
 * One entry per live entrypoint, and no more. `knip` cannot see an unused member of an exported
 * object, so a speculative label sits here looking supported until someone reports on it and finds
 * it never appears — add the value in the same change as the call site that sends it.
 */
export const ACCOUNTING_SETUP_HELP_CONTEXT = {
    connectAccounts: 'Connect Accounts',
    connectStatements: 'Connect Statements',
    statementUpload: 'Statement Upload',
    importTransactions: 'Import Transactions',
    uploadDocuments: 'Upload Documents',
    reimbursement: 'Reimbursement',
    accountingProgress: 'Accounting Progress',
} as const;

export type AccountingSetupHelpContext =
    (typeof ACCOUNTING_SETUP_HELP_CONTEXT)[keyof typeof ACCOUNTING_SETUP_HELP_CONTEXT];

interface OpenAccountingSetupHelpArgs {
    /**
     * The member-facing question. Travels verbatim as `metadata.question`, and again appended
     * after ACCOUNTING_SETUP_OPENER in the opening message — so the AOP can answer the specific
     * question behind the entrypoint whether it reads the metadata or only the transcript.
     */
    question: string;
    context: AccountingSetupHelpContext;
}

/**
 * Returns a handler that opens the Decagon help popup for one Accounting Setup question.
 *
 * Same flow as useOpenTransactionHelp: create a conversation server-side so the metadata is
 * attached from the moment Decagon creates the record, then hand the id to the popup. `context`
 * is required rather than defaulted so a new entrypoint can't silently inherit another step's
 * label — the per-question reporting is the whole point of the entrypoint.
 *
 * Memoised on its inputs because every caller so far puts the handler in a `useCallback` dep list.
 * An un-memoised return is a fresh closure each render, which silently makes those wrappers
 * memoise nothing.
 */
export default function useOpenAccountingSetupHelp({ question, context }: OpenAccountingSetupHelpArgs) {
    const accessToken = useApiAccessToken();
    const { mutateAsync: generateSignature } = useGenerateSignature();
    const { mutateAsync: createNewConversation } = useNewConversation();
    const { data: memberObj } = useMember();
    const { closeHelpAgentWidget } = useIMC();
    const isPayrollDashboardV2 = useStatsigGate(FeatureGates.PayrollDashboardV2);
    const isPayrollDashboardV2ShowLegacy = useStatsigGate(FeatureGates.PayrollDashboardV2ShowLegacy);

    return useCallback(async () => {
        if (!memberObj?.id || !accessToken || !memberObj?.membership_tier) {
            sendDatadogRumError(
                'useOpenAccountingSetupHelp: Missing required user information to open popup.',
                new Error('Missing required user information'),
                { memberId: memberObj?.id }
            );
            return;
        }

        // Sent twice on purpose: once so Decagon has it from the moment the conversation record
        // exists, and again through the popup because its setMetadata replaces the whole object.
        const metadata = {
            aop_name: ACCOUNTING_SETUP_AOP_NAME,
            page: ACCOUNTING_SETUP_PAGE,
            context,
            question,
        };

        try {
            const { conversation_id: conversationId } = await createNewConversation({
                id: memberObj.id.toString(),
                metadata,
            });

            openHelpAgentPopup(
                conversationId,
                `${ACCOUNTING_SETUP_OPENER} ${question}`,
                () => {},
                memberObj,
                accessToken,
                generateSignature,
                closeHelpAgentWidget,
                metadata,
                undefined,
                isPayrollDashboardV2,
                isPayrollDashboardV2ShowLegacy
            );
        } catch (error) {
            sendDatadogRumError('useOpenAccountingSetupHelp: Failed to create new conversation.', error, {
                memberId: memberObj?.id,
            });
        }
    }, [
        memberObj,
        accessToken,
        context,
        question,
        createNewConversation,
        generateSignature,
        closeHelpAgentWidget,
        isPayrollDashboardV2,
        isPayrollDashboardV2ShowLegacy,
    ]);
}
